/**
 * What to show while a number is being paired (Phase 136).
 *
 * Pure, because the interesting parts are arithmetic about time and this is the sort of thing that
 * is wrong by one second, or by an hour, on exactly the day it matters.
 *
 * The screen used to have one word for the whole of pairing — *Aguardando leitura* — and a QR code
 * that sat there. Somebody whose phone was already listing the device as connected read that as
 * "nothing happened", scanned again, and eventually gave up. That is what the first number paired
 * in production did.
 */

export const SYNC_TYPICAL_SECONDS = 90;

/** Whole seconds until `when`, never negative; null when there is nothing to count towards. */
export function secondsUntil(when: string | null, now: Date = new Date()): number | null {
  if (!when) return null;

  const remaining = Math.ceil((new Date(when).getTime() - now.getTime()) / 1000);

  return Number.isFinite(remaining) ? Math.max(0, remaining) : null;
}

/** Seconds since `when`, never negative; null when it never happened. */
export function secondsSince(when: string | null, now: Date = new Date()): number | null {
  if (!when) return null;

  const elapsed = Math.floor((now.getTime() - new Date(when).getTime()) / 1000);

  return Number.isFinite(elapsed) ? Math.max(0, elapsed) : null;
}

/** `m:ss`, because a bare count of seconds is something people have to translate. */
export function countdown(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safe / 60);

  return `${minutes}:${String(safe % 60).padStart(2, '0')}`;
}

/**
 * How far along a sync appears to be, as a fraction.
 *
 * Deliberately an *appearance*: nothing reports real progress — WhatsApp Web's own loading percent
 * stops at 99 and stays there — so this is elapsed time against a typical sync, capped below the
 * end. A bar that reaches 100% and keeps waiting is a lie that costs more trust than no bar at all,
 * so this one crawls the last stretch instead of arriving.
 */
export function syncProgress(elapsedSeconds: number, typical = SYNC_TYPICAL_SECONDS): number {
  if (elapsedSeconds <= 0) return 0;

  const share = elapsedSeconds / typical;

  // Past the typical time it keeps moving, slowly, and never reaches 1.
  return share >= 1 ? Math.min(0.95, 0.8 + (share - 1) * 0.05) : share * 0.8;
}

export type PairingStage = 'AWAITING_SCAN' | 'SYNCING';

/**
 * What the person should be told, from what the server said.
 *
 * `expired` is its own answer rather than a kind of waiting: a code nobody can use any more must
 * stop being displayed as if scanning it would work.
 */
export function pairingView(
  session: {
    stage: PairingStage | null;
    pairing_code: string | null;
    pairing_expires_at: string | null;
    last_seen_at: string | null;
    connected_at: string | null;
  },
  now: Date = new Date(),
):
  | { kind: 'scan'; secondsLeft: number }
  | { kind: 'expired' }
  | { kind: 'syncing'; elapsed: number; progress: number }
  | null {
  if (session.stage === null) return null;

  if (session.stage === 'AWAITING_SCAN' && session.pairing_code) {
    const left = secondsUntil(session.pairing_expires_at, now);

    return left !== null && left > 0 ? { kind: 'scan', secondsLeft: left } : { kind: 'expired' };
  }

  const elapsed = secondsSince(session.last_seen_at, now) ?? 0;

  return { kind: 'syncing', elapsed, progress: syncProgress(elapsed) };
}
